import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Megaphone, Search, Send, X } from "lucide-react";
import type { AnnouncementTarget } from "@mvk/shared";
import { announcementsApi } from "../../api/notifications";
import { adminEventsApi } from "../../services/adminEventsApi";
import type { VisitorAccessRow } from "../../services/adminEventsApi";
import { ApiError } from "../../api/client";

type TargetTab = "all" | "exhibitors" | "visitors" | "user";

type PickedUser = {
  userId: string;
  fullName: string;
  phone: string | null;
};

/**
 * Organizer/curator broadcast: MAX bot DM + PWA push + in-app inbox.
 * Supports audience groups or one/several specific event participants.
 */
export function AnnouncementModal({
  eventId,
  onClose,
}: {
  eventId: string;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const [title, setTitle] = useState("Объявление организатора");
  const [target, setTarget] = useState<TargetTab>("all");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentInfo, setSentInfo] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<VisitorAccessRow[]>([]);
  const [picked, setPicked] = useState<PickedUser[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (target !== "user") return;
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const data = await adminEventsApi.listVisitorAccess(eventId, {
          q: debouncedQ || undefined,
          page: 1,
          pageSize: 20,
          authorized: "all",
          role: "all",
        });
        if (!cancelled) setHits(data.visitors);
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, target, debouncedQ]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.userId)), [picked]);

  const togglePick = (row: VisitorAccessRow) => {
    setPicked((prev) => {
      if (prev.some((p) => p.userId === row.userId)) {
        return prev.filter((p) => p.userId !== row.userId);
      }
      return [
        ...prev,
        { userId: row.userId, fullName: row.fullName || "Участник", phone: row.phone },
      ];
    });
  };

  const handleSend = async () => {
    const body = text.trim();
    if (!body) {
      setError("Введите текст объявления");
      return;
    }
    if (target === "user" && picked.length === 0) {
      setError("Выберите хотя бы одного получателя");
      return;
    }
    setSending(true);
    setError("");
    try {
      const apiTarget: AnnouncementTarget = target;
      const result = await announcementsApi.create(eventId, {
        target: apiTarget,
        title: title.trim() || "Объявление организатора",
        body,
        userIds: target === "user" ? picked.map((p) => p.userId) : undefined,
      });
      setSentInfo(
        `Отправлено ${result.recipients} получателям. MAX — в чат бота; PWA — уведомление в браузере.`,
      );
      setTimeout(onClose, 1600);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.code === "user_ids_required" || err.code === "announcement_no_recipients"
            ? "Получатель не найден среди доступов мероприятия"
            : err.message
          : err instanceof Error
            ? err.message
            : "Не удалось отправить";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const tabs: Array<[TargetTab, string]> = [
    ["all", "Всем"],
    ["exhibitors", "Экспонентам"],
    ["visitors", "Посетителям"],
    ["user", "Одному"],
  ];

  const canSend =
    Boolean(text.trim()) && !sending && (target !== "user" || picked.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[15px] text-gray-900">Объявление участникам</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
            aria-label="Закрыть"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] text-gray-500 mb-2 block">Кому отправить</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {tabs.map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setTarget(v)}
                  className={`py-2 rounded-lg text-[12px] transition-colors ${
                    target === v
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {target === "user" ? (
            <div className="space-y-2">
              <label className="text-[12px] text-gray-500 block">Найти получателя</label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ФИО, телефон, email…"
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
              {picked.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {picked.map((p) => (
                    <button
                      key={p.userId}
                      type="button"
                      onClick={() =>
                        setPicked((prev) => prev.filter((x) => x.userId !== p.userId))
                      }
                      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 text-[11px] px-2.5 py-1"
                      title="Убрать"
                    >
                      {p.fullName}
                      {p.phone ? ` · ${p.phone}` : ""}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50">
                {searching ? (
                  <div className="flex items-center justify-center gap-2 py-4 text-[12px] text-gray-400">
                    <Loader2 size={14} className="animate-spin" /> Поиск…
                  </div>
                ) : hits.length === 0 ? (
                  <p className="px-3 py-4 text-[12px] text-gray-400 text-center">
                    Никого не найдено. Проверьте доступы мероприятия.
                  </p>
                ) : (
                  hits.map((row) => {
                    const on = pickedIds.has(row.userId);
                    return (
                      <button
                        key={row.userId}
                        type="button"
                        onClick={() => togglePick(row)}
                        className={`w-full text-left px-3 py-2.5 transition-colors ${
                          on ? "bg-indigo-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="text-[13px] text-gray-900 truncate">
                          {row.fullName || "Без имени"}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {[row.phone, row.email, row.roles.join(", ")].filter(Boolean).join(" · ")}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              <p className="text-[11px] text-gray-400">
                Можно выбрать несколько человек из доступов этого мероприятия.
              </p>
            </div>
          ) : null}

          <div>
            <label className="text-[12px] text-gray-500 mb-1.5 block">Заголовок</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              maxLength={200}
            />
          </div>
          <div>
            <label className="text-[12px] text-gray-500 mb-1.5 block">Текст объявления</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Например: «Выставка продлена на 1 час» или «Потерялся ребёнок у павильона 2…»"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
            />
            <div className="text-right text-[11px] text-gray-400 mt-1">{text.length} символов</div>
          </div>
          <div className="p-3 rounded-xl bg-indigo-50 text-[12px] text-indigo-700 space-y-1">
            <p>
              <strong>MAX:</strong> сообщение в личный чат с ботом MVK One (+ кнопка «Открыть»).
            </p>
            <p>
              <strong>PWA / браузер:</strong> push, если разрешены уведомления.
            </p>
            <p className="text-indigo-600/80">Также во входящих уведомлениях приложения.</p>
          </div>
          {error ? <p className="text-[12px] text-red-600">{error}</p> : null}
          {sentInfo ? <p className="text-[12px] text-emerald-700">{sentInfo}</p> : null}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={!canSend}
            onClick={() => void handleSend()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Отправка…" : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AnnouncementLaunchButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        className ||
        "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white transition-colors text-left"
      }
    >
      <Megaphone size={15} />
      <span className="text-[13px]">Отправить объявление</span>
    </button>
  );
}
